import React from 'react';
import { UserProfile } from '../../../types/api/UserProfile';
import { Icons, SVG } from '../../../components/atoms/SVG';
import ILink from '../../../types/ILink';
import { Hyperlink } from '../../../components/atoms/Hyperlink';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { DateFormat, DateFormats } from '../../../_lib/components/DateFormat';
import { ISonURLDeleted } from '../../../types/IUrl';
import { useGlobalPrintMode } from '../../../contexts/GlobalPrintMode';
import { ThreadLastActivityInfo } from './ThreadLastActivityInfo';

type TeaserImage = {
  imageId: string;
  originalFilename: string;
  availableWidths: [320];
  alt: string;
  copyright: string;
  imageType: string;
  contentType: string;
  imageState: 'ready' | 'pending';
  orientation: 'landscape';
  src: string;
};

export type ThreadLastActivityInfoProps = {
  author: UserProfile;
  activityDate: string;
  activityUrl: string;
};

export type ThreadInformationProps = {
  id: string;
  createDate: string;
  title: string;
  pageLink: ILink;
  teaserText: string;
  author: ISonURLDeleted;
  activityCount: number;
  lastActivityInfo?: ThreadLastActivityInfoProps;
  teaserImage: TeaserImage;
};

export function ThreadInformation({
  id,
  createDate,
  title,
  pageLink,
  teaserText,
  author,
  activityCount,
  lastActivityInfo,
  teaserImage,
}: ThreadInformationProps) {
  const { t } = useSonTranslation('ThreadlistBlock');
  const { isInGlobalPrintMode } = useGlobalPrintMode();

  const getCommentIcon = () => {
    return (
      <span className="thread-list__activity-icon">
        <SVG svgSpriteId={Icons.COMMENTS} />
      </span>
    );
  };

  const getActivityInfo = () => {
    switch (activityCount) {
      case 0:
        return t('activityInfo.none');
      case 1:
        return t('activityInfo.one');
      default:
        return t('activityInfo.multiple', { count: activityCount });
    }
  };

  const getAuthor = () => {
    if (author.url) {
      return (
        <Hyperlink to={author.url} className="thread-list__author font-bold-15-19">
          {author.name}
        </Hyperlink>
      );
    }
    if (author.deletedUser) {
      return (
        <strong className="font-bold-15-19">
          {author.name ? `${author.name} (${t('deleted')})` : t('deletedUser')}
        </strong>
      );
    }
  };

  return (
    <li className="thread-list__thread" id={id}>
      <div className="thread-list__thread-container">
        <div className="thread-list__teaserimage-container">
          <Hyperlink to={pageLink.url} tabIndex={-1} ariaHidden className="thread-list__image-link">
            <img
              src={teaserImage.src}
              loading={isInGlobalPrintMode ? undefined : 'lazy'}
              alt={teaserImage.alt}
              className="thread-list__teaserimage"
            />
          </Hyperlink>
        </div>
        <div className="thread-list__thread-inner-container">
          <div className="thread-list__information">
            <div className="thread-list__information-column">
              <h3 className="thread-list__title">
                <Hyperlink to={pageLink.url} className="thread-list__title-link font-bold-16-19">
                  {title}
                </Hyperlink>
              </h3>
              <p className="thread-list__time-container">
                <dfn className="invisible">{t('created')}: </dfn>
                <DateFormat
                  date={createDate}
                  format={DateFormats.DATE}
                  className="thread-list__time font-regular-15-19"
                />
                <span className="font-regular-15-19">{` ${t('from')} `}</span>
                {getAuthor()}
              </p>
              <p className="thread-list__activity-container">
                {getCommentIcon()}
                <span className="font-regular-15-19">{getActivityInfo()}</span>
              </p>
            </div>
            {lastActivityInfo && (
              <div className="thread-list__information-column">
                <h4 className="thread-list__last-activity-headline">
                  <Hyperlink
                    to={lastActivityInfo.activityUrl}
                    routing={false}
                    className="thread-list__last-activity font-bold-15-19"
                  >
                    {t('lastActivity')}<span className="invisible"> {t('inThread')} „{title}“</span>
                  </Hyperlink>
                </h4>
                <ThreadLastActivityInfo lastActivityInfo={lastActivityInfo} />
              </div>
            )}
          </div>
          {teaserText && <p className="thread-list__teaser-text font-regular-15-19">{teaserText}</p>}
        </div>
      </div>
    </li>
  );
}
